import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  defaultOppTopExchanges,
  getOppOrderbookExchanges,
  getOppSpreadsTop,
  type OppSpreadItem,
  type OppSpreadTopSortBy,
} from '@/services/oppDetectorApi';
import { hasCategory, normalizeCategory, renderCategoryLabel, categoryBadgeClass } from '@/lib/instrumentCategory';
import './OppTop.css';

const DEFAULT_TOP_LIMIT = 100;
const POLL_MS = 10000;
const DEFAULT_WINDOW_SECONDS = 60;
type ExchangeFocusMode = 'related' | 'between';

const SORT_OPTIONS: Array<{ value: OppSpreadTopSortBy; label: string }> = [
  { value: 'spread', label: '当前价差' },
  { value: 'abs_spread', label: '当前价差绝对值' },
  { value: 'relative_open_line', label: '相对 open line' },
];

function formatSpreadPct(spread: number): string {
  if (!Number.isFinite(spread)) return '—';
  return `${(spread * 100).toFixed(4)}%`;
}

function formatRatio(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${ratio.toFixed(3)}x`;
}

function formatSignedSpreadPct(spread: number): string {
  if (!Number.isFinite(spread)) return '—';
  const pct = spread * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(4)}%`;
}

function formatPrice(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 6 });
  if (a >= 1e-4) return v.toLocaleString('en-US', { maximumFractionDigits: 8 });
  return v.toExponential(4);
}

function formatTsUs(tsUs: number): string {
  if (!Number.isFinite(tsUs) || tsUs <= 0) return '—';
  const ms = Math.floor(tsUs / 1000);
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function toUiExchange(exchange: string): string {
  const normalized = exchange.trim().toLowerCase();
  if (normalized === 'okx') return 'OKX';
  if (normalized === 'bybit') return 'Bybit';
  if (normalized === 'binance') return 'Binance';
  if (normalized === 'bitget') return 'Bitget';
  if (normalized === 'gateio') return 'Gateio';
  if (normalized === 'hyperliquid') return 'Hyperliquid';
  if (normalized === 'aster') return 'Aster';
  if (normalized === 'edgex') return 'EdgeX';
  if (normalized === 'lighter') return 'Lighter';
  if (normalized === 'paradex') return 'Paradex';
  return exchange;
}

export function OppTop() {
  const [rows, setRows] = useState<OppSpreadItem[]>([]);
  const [exchangeOptions, setExchangeOptions] = useState<string[]>([]);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>([]);
  const [exchangesReady, setExchangesReady] = useState(false);
  const [focusExchange1, setFocusExchange1] = useState('');
  const [focusExchange2, setFocusExchange2] = useState('');
  const [focusMode, setFocusMode] = useState<ExchangeFocusMode>('related');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [topLimit, setTopLimit] = useState(DEFAULT_TOP_LIMIT);
  const [windowSeconds, setWindowSeconds] = useState(DEFAULT_WINDOW_SECONDS);
  const [sortBy, setSortBy] = useState<OppSpreadTopSortBy>('spread');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    void getOppOrderbookExchanges()
      .then((list) => {
        if (!mounted.current) return;
        setExchangeOptions(list);
        setSelectedExchanges((prev) => {
          if (prev.length > 0) {
            return prev.filter((exchange) => list.includes(exchange));
          }
          return defaultOppTopExchanges(list);
        });
        setExchangesReady(true);
      })
      .catch((e) => {
        if (!mounted.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  const toggleExchange = useCallback((exchange: string) => {
    setSelectedExchanges((prev) => (
      prev.includes(exchange)
        ? prev.filter((item) => item !== exchange)
        : [...prev, exchange]
    ));
  }, []);

  const load = useCallback(async () => {
    if (!exchangesReady) return;

    if (selectedExchanges.length === 0) {
      setRows([]);
      setLoading(false);
      setError('请至少选择一个交易所');
      return;
    }

    try {
      const data = await getOppSpreadsTop(topLimit, windowSeconds, sortBy, selectedExchanges);
      if (!mounted.current) return;
      setRows(data);
      setError(null);
      setLastOkAt(Date.now());
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [exchangesReady, selectedExchanges, sortBy, topLimit, windowSeconds]);

  useEffect(() => {
    if (!exchangesReady) return;
    mounted.current = true;
    setLoading(true);
    void load();
    const t = autoRefresh ? window.setInterval(() => void load(), POLL_MS) : null;
    return () => {
      mounted.current = false;
      if (t != null) window.clearInterval(t);
    };
  }, [autoRefresh, exchangesReady, load]);

  const rowExchangeOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.flatMap((item) => [item.buy_exchange, item.sell_exchange]).filter((exchange) => exchange.trim().length > 0)),
      ).sort((a, b) => a.localeCompare(b)),
    [rows],
  );

  const categoryOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((item) => normalizeCategory(item.category)).filter((type) => type.length > 0))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [rows],
  );

  useEffect(() => {
    if (!focusExchange1 || rowExchangeOptions.includes(focusExchange1)) return;
    setFocusExchange1('');
  }, [rowExchangeOptions, focusExchange1]);

  useEffect(() => {
    if (!focusExchange2 || rowExchangeOptions.includes(focusExchange2)) return;
    setFocusExchange2('');
  }, [rowExchangeOptions, focusExchange2]);

  useEffect(() => {
    if (!categoryFilter || categoryOptions.includes(categoryFilter)) return;
    setCategoryFilter('');
  }, [categoryFilter, categoryOptions]);

  const filteredRows = useMemo(() => {
    const ex1 = focusExchange1.trim();
    const ex2 = focusExchange2.trim();
    const tag = categoryFilter.trim();
    const tagMatchedRows = tag ? rows.filter((item) => normalizeCategory(item.category) === tag) : rows;
    if (!ex1 || !ex2 || ex1 === ex2) return tagMatchedRows;
    const include = (exchange: string) => exchange === ex1 || exchange === ex2;
    if (focusMode === 'related') {
      return tagMatchedRows.filter((item) => include(item.buy_exchange) || include(item.sell_exchange));
    }
    return tagMatchedRows.filter((item) => include(item.buy_exchange) && include(item.sell_exchange));
  }, [focusExchange1, focusExchange2, focusMode, rows, categoryFilter]);

  const jumpToOppSpreadLive = useCallback((item: OppSpreadItem) => {
    const params = new URLSearchParams({
      symbol: item.symbol,
      exchange1: toUiExchange(item.buy_exchange),
      exchange2: toUiExchange(item.sell_exchange),
      autoQuery: '1',
    });
    const url = `${window.location.origin}/trading?${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const marketSummary = useMemo(() => {
    const topSpread = filteredRows.reduce<OppSpreadItem | null>((best, row) => {
      if (!best) return row;
      return Math.abs(row.spread) > Math.abs(best.spread) ? row : best;
    }, null);
    const positiveCount = filteredRows.filter((row) => row.spread > 0).length;
    return {
      topSpread,
      positiveCount,
      exchangeCount: selectedExchanges.length,
      categoryCount: categoryOptions.length,
    };
  }, [categoryOptions.length, filteredRows, selectedExchanges.length]);

  return (
    <div className="opp-top-page">
      <header className="opp-top-header">
        <div className="opp-top-header-copy">
          <h2>跨所价差市场</h2>
        </div>
        <div className="opp-top-header-metrics" aria-label="市场概览">
          <div className="opp-top-header-metric primary">
            <span>最强机会</span>
            <strong>{marketSummary.topSpread ? marketSummary.topSpread.symbol : '—'}</strong>
            <em>
              {marketSummary.topSpread
                ? `${marketSummary.topSpread.buy_exchange} → ${marketSummary.topSpread.sell_exchange} · ${formatSignedSpreadPct(marketSummary.topSpread.spread)}`
                : '暂无价差'}
            </em>
          </div>
          <div className="opp-top-header-metric">
            <span>展示</span>
            <strong>{filteredRows.length}</strong>
            <em>来自 {rows.length}</em>
          </div>
          <div className="opp-top-header-metric">
            <span>正向</span>
            <strong>{marketSummary.positiveCount}</strong>
            <em>筛选内</em>
          </div>
          <div className="opp-top-header-metric">
            <span>交易所/类别</span>
            <strong>{marketSummary.exchangeCount} / {marketSummary.categoryCount}</strong>
            <em>已选</em>
          </div>
        </div>
        <div className="opp-top-live-card">
          <span className={`opp-top-live-dot${autoRefresh ? ' is-live' : ''}`} />
          <div>
            <strong>{autoRefresh ? `${POLL_MS / 1000}s 自动刷新` : '手动刷新'}</strong>
            <span>{lastOkAt != null ? `上次成功 ${new Date(lastOkAt).toLocaleTimeString('zh-CN', { hour12: false })}` : '等待数据'}</span>
          </div>
        </div>
      </header>

      <section className="opp-top-toolbar">
        <div className="opp-top-toolbar-actions">
          <button type="button" className="primary" disabled={loading} onClick={() => void load()}>
            {loading ? '刷新中…' : '立即刷新'}
          </button>
          <button type="button" className="secondary" onClick={() => setAutoRefresh((v) => !v)}>
            {autoRefresh ? '停止自动刷新' : '启动自动刷新'}
          </button>
        </div>
        <div className="opp-funding-exchange-checkboxes">
          <span className="opp-funding-exchange-checkboxes-label">交易所（至少 1 个）</span>
          <div className="opp-funding-exchange-checkboxes-list">
            {exchangeOptions.map((exchange) => (
              <label key={exchange} className="opp-funding-exchange-checkbox">
                <input
                  type="checkbox"
                  checked={selectedExchanges.includes(exchange)}
                  onChange={() => toggleExchange(exchange)}
                />
                <span>{exchange}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="opp-top-filter-grid">
          <label className="opp-top-filter-field">
            <span>TopN 数量</span>
            <input
              type="number"
              min={1}
              step={1}
              value={topLimit}
              onChange={(e) => {
                const next = Number(e.target.value);
                setTopLimit(Number.isFinite(next) && next > 0 ? Math.floor(next) : DEFAULT_TOP_LIMIT);
              }}
            />
          </label>
          <label className="opp-top-filter-field">
            <span>时间窗口(秒)</span>
            <input
              type="number"
              min={1}
              step={1}
              value={windowSeconds}
              onChange={(e) => {
                const next = Number(e.target.value);
                setWindowSeconds(Number.isFinite(next) && next > 0 ? Math.floor(next) : DEFAULT_WINDOW_SECONDS);
              }}
            />
          </label>
          <label className="opp-top-filter-field">
            <span>排序</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as OppSpreadTopSortBy)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="opp-top-filter-field">
            <span>类别</span>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="">全部</option>
              {categoryOptions.map((type) => (
                <option key={type} value={type}>
                  {renderCategoryLabel(type)}
                </option>
              ))}
            </select>
          </label>
          <label className="opp-top-filter-field">
            <span>关注交易所 1</span>
            <select value={focusExchange1} onChange={(e) => setFocusExchange1(e.target.value)}>
              <option value="">全部</option>
              {rowExchangeOptions.map((exchange) => (
                <option key={exchange} value={exchange}>
                  {exchange}
                </option>
              ))}
            </select>
          </label>
          <label className="opp-top-filter-field">
            <span>关注交易所 2</span>
            <select value={focusExchange2} onChange={(e) => setFocusExchange2(e.target.value)}>
              <option value="">全部</option>
              {rowExchangeOptions.map((exchange) => (
                <option key={exchange} value={exchange}>
                  {exchange}
                </option>
              ))}
            </select>
          </label>
          <label className="opp-top-filter-field wide">
            <span>筛选范围</span>
            <select value={focusMode} onChange={(e) => setFocusMode(e.target.value as ExchangeFocusMode)}>
              <option value="related">两个交易所相关的所有机会</option>
              <option value="between">仅两个交易所之间的机会</option>
            </select>
          </label>
          <button
            type="button"
            className="ghost"
            onClick={() => { setFocusExchange1(''); setFocusExchange2(''); setFocusMode('related'); setCategoryFilter(''); }}
          >
            清空筛选
          </button>
        </div>
      </section>

      {error && <div className="opp-top-error">{error}</div>}

      <section className="opp-top-table-card">
        <div className="opp-top-table-head">
          <div>
            <p className="opp-top-kicker">Opportunity board</p>
            <h3>价差机会列表</h3>
          </div>
          <p className="opp-top-meta">点击任意行进入交易页并自动查询</p>
        </div>
        <div className="opp-top-table-wrap">
        {filteredRows.length === 0 && !loading && !error ? (
          <div className="opp-top-empty">暂无数据（服务未启动或尚未产生价差条目）</div>
        ) : (
          <table className="opp-top-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Symbol</th>
                <th>买所 / 卖所</th>
                <th>买 leg</th>
                <th>卖 leg</th>
                <th className="num">ask 买</th>
                <th className="num">bid 卖</th>
                <th className="num">Spread</th>
                <th className="num">Open line</th>
                <th className="num">相对值</th>
                <th>更新时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => (
                <tr
                  key={`${r.symbol}-${r.buy_exchange}-${r.sell_exchange}-${i}`}
                  className="opp-top-row-clickable"
                  onClick={() => jumpToOppSpreadLive(r)}
                  title="点击跳转到 Opp Spread/K 线并自动查询"
                >
                  <td>{i + 1}</td>
                  <td>
                    <span className="opp-top-symbol-cell">
                      <span>{r.symbol}</span>
                      {hasCategory(r.category) && (
                        <span className={categoryBadgeClass(r.category)}>{renderCategoryLabel(r.category)}</span>
                      )}
                    </span>
                  </td>
                  <td>
                    {r.buy_exchange} → {r.sell_exchange}
                  </td>
                  <td>
                    <span title={r.buy_inst_id}>{r.buy_inst_id}</span>
                  </td>
                  <td>
                    <span title={r.sell_inst_id}>{r.sell_inst_id}</span>
                  </td>
                  <td className="num">{formatPrice(r.ask_buy)}</td>
                  <td className="num">{formatPrice(r.bid_sell)}</td>
                  <td className={`num ${r.spread >= 0 ? 'opp-top-positive' : 'opp-top-negative'}`}>{formatSignedSpreadPct(r.spread)}</td>
                  <td className="num">{r.open_line == null ? '—' : formatSpreadPct(r.open_line)}</td>
                  <td className="num">{formatRatio(r.open_line_ratio)}</td>
                  <td>{formatTsUs(r.ts_us)}</td>
                  <td>
                    <span className="opp-top-row-action">交易</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </section>
    </div>
  );
}
